import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { ProjectListItem, TourListItem } from "../../shared/api-types";
import { apiError, useT } from "../i18n";
import { apiJson } from "./api";

// Projekte samt Touren liegen an EINER Stelle: die Seitenleiste zeigt sie
// dauerhaft, Übersicht und Projektseite lesen dieselbe Liste. Ohne das würde
// jede Seite ihre eigene Kopie laden und die Leiste ginge nach Änderungen
// auseinander.

type ProjectsState = {
  /** null = noch nicht geladen */
  projects: ProjectListItem[] | null;
  error: string | null;
  reload: () => void;
};

const ProjectsContext = createContext<ProjectsState>({
  projects: null,
  error: null,
  reload: () => {},
});

export function ProjectsProvider({
  enabled = true,
  children,
}: {
  /** Ohne Account gibt es keine Projekte — dann gar nicht erst laden */
  enabled?: boolean;
  children: ReactNode;
}) {
  const [projects, setProjects] = useState<ProjectListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  const reload = useCallback(() => {
    if (!enabled) return;
    apiJson<ProjectListItem[]>("/api/projects")
      .then((rows) => {
        setProjects(rows);
        setError(null);
      })
      .catch((e: Error) => setError(apiError(t, e.message)));
    // t wechselt nur mit der Sprache — die Liste selbst hängt nicht daran
  }, [enabled]);

  useEffect(reload, [reload]);

  const value = useMemo(() => ({ projects, error, reload }), [projects, error, reload]);
  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>;
}

export function useProjects(): ProjectsState {
  return useContext(ProjectsContext);
}

/** Projekte alphabetisch — Navigation soll auffindbar sein, nicht chronologisch. */
export function byName(projects: ProjectListItem[]): ProjectListItem[] {
  return [...projects].sort((a, b) => a.name.localeCompare(b.name));
}

/** In welchem Projekt steckt diese Tour? Für Aufklappen und Brotkrumen. */
export function findTour(
  projects: ProjectListItem[] | null,
  tourId: string,
): { project: ProjectListItem; tour: TourListItem } | null {
  for (const project of projects ?? []) {
    const tour = project.tours.find((x) => x.id === tourId);
    if (tour) return { project, tour };
  }
  return null;
}
